import * as React from 'react'
import { Popover as PopoverPrimitive } from '@base-ui/react/popover'
import { cn } from '@/shared/lib/utils'

function isLinkLikeElement(element: React.ReactElement): boolean {
  if (element.type === 'a') {
    return true
  }

  const props = element.props as { to?: unknown; href?: unknown }
  return props.to != null || props.href != null
}

function resolveNativeButton(
  element: unknown,
  defaultWhenNoRender: boolean
): boolean {
  if (element == null) {
    return defaultWhenNoRender
  }

  if (!React.isValidElement(element)) {
    return defaultWhenNoRender
  }

  if (element.type === 'button') {
    return true
  }

  if (isLinkLikeElement(element)) {
    return false
  }

  if (typeof element.type === 'string') {
    return false
  }

  // Custom components (e.g. Button) typically render <button>.
  return true
}

function Popover({ ...props }: PopoverPrimitive.Root.Props) {
  return <PopoverPrimitive.Root data-slot='popover' {...props} />
}

function PopoverTrigger({
  asChild = false,
  render,
  children,
  nativeButton,
  ...props
}: PopoverPrimitive.Trigger.Props & {
  asChild?: boolean
}) {
  let resolvedRender = render
  let resolvedChildren = children

  if (asChild) {
    const child = React.Children.only(children) as React.ReactElement<{
      children?: React.ReactNode
    }>
    resolvedRender = child
    resolvedChildren = child.props.children
  }

  const resolvedNativeButton =
    nativeButton ??
    resolveNativeButton(
      resolvedRender,
      /* default trigger renders <button> */ true
    )

  return (
    <PopoverPrimitive.Trigger
      data-slot='popover-trigger'
      nativeButton={resolvedNativeButton}
      render={resolvedRender}
      {...props}
    >
      {resolvedChildren}
    </PopoverPrimitive.Trigger>
  )
}

function PopoverContent({
  className,
  align = 'center',
  alignOffset = 0,
  side = 'bottom',
  sideOffset = 4,
  children,
  ...props
}: PopoverPrimitive.Popup.Props &
  Pick<
    PopoverPrimitive.Positioner.Props,
    'align' | 'alignOffset' | 'side' | 'sideOffset'
  >) {
  return (
    <PopoverPrimitive.Portal className='pointer-events-auto z-[100]'>
      <PopoverPrimitive.Positioner
        data-slot='popover-positioner'
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
        className='pointer-events-auto isolate z-[100]'
      >
        <PopoverPrimitive.Popup
          data-slot='popover-content'
          className={cn(
            'pointer-events-auto z-[100] w-72 origin-(--transform-origin) rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-hidden data-[side=bottom]:slide-in-from-top-2 data-[side=inline-end]:slide-in-from-left-2 data-[side=inline-start]:slide-in-from-right-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
            className
          )}
          {...props}
        >
          {children}
        </PopoverPrimitive.Popup>
      </PopoverPrimitive.Positioner>
    </PopoverPrimitive.Portal>
  )
}

/** Radix-compat anchor stub (unused in app; Base UI positions against the trigger). */
function PopoverAnchor({
  ...props
}: React.ComponentProps<'div'>) {
  return <div data-slot='popover-anchor' {...props} />
}

export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor }
